import { Fragment, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Check,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plane,
  Plus,
  RotateCcw,
  Trash2,
  Users,
  Video,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AvailabilityStatus,
  LocationType,
  ProjectAvailability,
  ProjectFunctionDay,
  ProjectFunctionGroup,
  ProjectFunctionSeries,
  ProjectPersonTravelDay,
} from "@/hooks/useProjectPeople";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePeople, type PersonWithCompany } from "@/hooks/usePeople";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  EntityCombobox,
  type ComboboxOption,
} from "@/components/ui/entity-combobox";
import { AvailabilityStatusBadge } from "./AvailabilityStatusBadge";
import { PersonHoverCard } from "@/components/people/PersonHoverCard";
import { cn } from "@/lib/utils";
import {
  getMainFunctionGroupForPerson,
  sortByMainFunctionGroup,
} from "@/lib/people-planning-groups";
import {
  getPersonInitials,
  useHorizontalScrollState,
} from "./pinnedColumnUtils";

export type FunctionViewMode = "person" | "function";

interface FunctionAssignmentMatrixProps {
  functions: ProjectFunctionSeries[];
  groups: ProjectFunctionGroup[];
  travelDays: ProjectPersonTravelDay[];
  availability: ProjectAvailability[];
  planningDays: Date[];
  eventDays: Date[];
  visibleDays: Date[];
  viewMode: FunctionViewMode;
  onAssign: (functionId: string, personId: string, remark?: string) => void;
  onBulkAssign: (
    seriesId: string,
    personId: string,
    functionIds: string[],
  ) => void;
  onBookAssignedPerson: (personId: string, functionDate: string) => void;
  onUnassign: (assignmentId: string) => void;
  onUpdateRemark: (assignmentId: string, remark: string | null) => void;
  onDeleteSeries: (seriesId: string) => void;
  onUpdateSeries: (
    seriesId: string,
    updates: {
      name?: string;
      project_function_group_id?: string | null;
      required_count?: number;
      selected_days?: string[];
      system_tag?: string | null;
      location_type?: LocationType;
    },
  ) => void;
  onUpdateDayOverride: (
    dayId: string,
    requiredCountOverride: number | null,
  ) => void;
  onUpdateGroup?: (
    groupId: string,
    updates: { name?: string; location_type?: LocationType },
  ) => void;
  onCreateFunction?: (data: {
    name: string;
    project_function_group_id?: string | null;
    required_count: number;
    selected_days: string[];
    system_tag?: string | null;
    location_type?: LocationType;
  }) => void;
  onToggleTravelDay?: (personId: string, date: string) => void;
  cameraNumberByAssignmentId?: Map<string, string> | Record<string, string>;
  isUpdating?: boolean;
}

function BulkAssignIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="9.5" cy="7" r="4" />
      <path d="M20 8v6" />
      <path d="M17 11h6" />
      <path d="M16 15.5a4 4 0 0 1 3.5 4" />
      <path d="M16 3.5a4 4 0 0 1 0 7" />
    </svg>
  );
}

interface SeriesEditorState {
  id: string;
  name: string;
  groupId: string | null;
  requiredCount: number;
  selectedDays: string[];
  dayOverrides: Record<string, number | null>;
  systemTag: string | null;
  locationType: LocationType;
}

interface AssignmentCandidate {
  personId: string;
  label: string;
  subtitle?: string;
  pool: "booked" | "project" | "global";
}

function dayToString(day: Date) {
  return format(day, "yyyy-MM-dd");
}

function getEffectiveRequiredCount(
  series: ProjectFunctionSeries,
  day: ProjectFunctionDay,
) {
  return day.required_count_override ?? series.required_count;
}

function getCandidateLabel(
  person:
    | ProjectAvailability["person"]
    | Pick<PersonWithCompany, "first_name" | "last_name">,
) {
  return `${person?.first_name ?? ""} ${person?.last_name ?? ""}`.trim();
}

function getAvailabilityLabel(availability: ProjectAvailability) {
  return availability.person
    ? getCandidateLabel(availability.person)
    : "Unknown";
}

function sortCandidates(candidates: AssignmentCandidate[]) {
  return [...candidates].sort((a, b) => a.label.localeCompare(b.label));
}

function getVisibleCandidateOptions(options: ComboboxOption[], search: string) {
  const normalizedSearch = search.trim().toLowerCase();
  const matches = (option: ComboboxOption) => {
    if (!normalizedSearch) return true;
    return `${option.label} ${option.subtitle ?? ""}`
      .toLowerCase()
      .includes(normalizedSearch);
  };

  const booked = options.filter(
    (option) => option.group === "booked" && matches(option),
  );
  if (booked.length > 0) return booked;

  const project = options.filter(
    (option) => option.group === "project" && matches(option),
  );
  if (project.length > 0) return project;

  return options.filter(
    (option) => option.group === "global" && matches(option),
  );
}

function formatCameraAssignmentTag(cameraNumber: string) {
  return `CAM${cameraNumber}`;
}

function getCameraNumberForAssignment(
  cameraNumberByAssignmentId:
    | Map<string, string>
    | Record<string, string>
    | undefined,
  assignmentId: string,
) {
  if (!cameraNumberByAssignmentId) return undefined;
  if (cameraNumberByAssignmentId instanceof Map) {
    return cameraNumberByAssignmentId.get(assignmentId);
  }
  return cameraNumberByAssignmentId[assignmentId];
}

function SeriesEditorDialog({
  open,
  onOpenChange,
  state,
  planningDays,
  eventDays,
  existingDays,
  groups,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: SeriesEditorState | null;
  planningDays: Date[];
  eventDays: Date[];
  existingDays: Map<string, ProjectFunctionDay>;
  groups: ProjectFunctionGroup[];
  onSave: (state: SeriesEditorState) => void;
}) {
  const [localState, setLocalState] = useState<SeriesEditorState | null>(state);

  useEffect(() => {
    setLocalState(state);
  }, [state]);

  const planningOptions = useMemo(
    () =>
      planningDays.map((day) => ({
        value: dayToString(day),
        label: format(day, "EEEE, dd.MM.yyyy"),
      })),
    [planningDays],
  );
  const eventDayValues = useMemo(
    () => eventDays.map((day) => dayToString(day)),
    [eventDays],
  );

  if (!localState) return null;

  const toggleDay = (day: string) => {
    setLocalState((current) =>
      current
        ? {
            ...current,
            selectedDays: current.selectedDays.includes(day)
              ? current.selectedDays.filter((value) => value !== day)
              : [...current.selectedDays, day].sort(),
          }
        : current,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="function-series-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit Function</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-function-name">Function Name</Label>
            <Input
              id="edit-function-name"
              data-testid="function-series-editor-name"
              value={localState.name}
              onChange={(e) =>
                setLocalState({ ...localState, name: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-function-group">Group</Label>
            <Select
              value={localState.groupId ?? "none"}
              onValueChange={(v) =>
                setLocalState({
                  ...localState,
                  groupId: v === "none" ? null : v,
                })
              }
            >
              <SelectTrigger id="edit-function-group">
                <SelectValue placeholder="No group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No group</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-required-count">Default Required People</Label>
            <Input
              id="edit-required-count"
              data-testid="function-series-editor-required-count"
              type="number"
              min={1}
              value={localState.requiredCount}
              onChange={(e) =>
                setLocalState({
                  ...localState,
                  requiredCount: parseInt(e.target.value) || 1,
                })
              }
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="edit-camera-position"
              checked={localState.systemTag === "camera_position"}
              onCheckedChange={(checked) =>
                setLocalState({
                  ...localState,
                  systemTag: checked ? "camera_position" : null,
                })
              }
            />
            <Label htmlFor="edit-camera-position" className="cursor-pointer">
              Camera position
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-location-type">Location</Label>
            <Select
              value={localState.locationType ?? "none"}
              onValueChange={(v) =>
                setLocalState({
                  ...localState,
                  locationType:
                    v === "none" ? null : (v as "remote" | "on_site"),
                })
              }
            >
              <SelectTrigger id="edit-location-type">
                <SelectValue placeholder="Not specified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use group location</SelectItem>
                <SelectItem value="on_site">On-site</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label>Selected Days</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="function-series-editor-select-all-planning-days"
                  onClick={() =>
                    setLocalState({
                      ...localState,
                      selectedDays: planningOptions.map(
                        (option) => option.value,
                      ),
                    })
                  }
                >
                  All planning days
                </Button>
                {eventDayValues.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="function-series-editor-select-event-days"
                    onClick={() =>
                      setLocalState({
                        ...localState,
                        selectedDays: eventDayValues,
                      })
                    }
                  >
                    Event dates
                  </Button>
                )}
              </div>
            </div>

            <ScrollArea className="h-56 rounded-md border p-3">
              <div className="space-y-2">
                {planningOptions.map((option) => {
                  const isSelected = localState.selectedDays.includes(
                    option.value,
                  );
                  const existingDay = existingDays.get(option.value);
                  const overrideValue = localState.dayOverrides[option.value];

                  return (
                    <div
                      key={option.value}
                      data-testid={`function-series-editor-day-option-${option.value}`}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50"
                    >
                      <label className="flex flex-1 items-center gap-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleDay(option.value)}
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                      {isSelected && (
                        <div className="flex items-center gap-1">
                          <Input
                            data-testid={
                              existingDay
                                ? `function-series-day-override-input-${existingDay.id}`
                                : `function-series-day-override-input-new-${option.value}`
                            }
                            type="number"
                            min={1}
                            placeholder={String(localState.requiredCount)}
                            value={overrideValue ?? ""}
                            onChange={(e) => {
                              const val = e.target.value
                                ? parseInt(e.target.value) || null
                                : null;
                              setLocalState((current) =>
                                current
                                  ? {
                                      ...current,
                                      dayOverrides: {
                                        ...current.dayOverrides,
                                        [option.value]: val,
                                      },
                                    }
                                  : current,
                              );
                            }}
                            className="h-7 w-16 text-xs"
                          />
                          {overrideValue != null && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="Reset to default"
                              onClick={() =>
                                setLocalState((current) =>
                                  current
                                    ? {
                                        ...current,
                                        dayOverrides: {
                                          ...current.dayOverrides,
                                          [option.value]: null,
                                        },
                                      }
                                    : current,
                                )
                              }
                            >
                              <RotateCcw className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              data-testid="function-series-editor-cancel"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="function-series-editor-save"
              onClick={() => onSave(localState)}
              disabled={
                !localState.name.trim() || localState.selectedDays.length === 0
              }
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentAutocomplete({
  candidates,
  onAssign,
  placeholder,
  testId,
  keepOpenOnSelect,
}: {
  candidates: AssignmentCandidate[];
  onAssign: (personId: string) => void;
  placeholder: string;
  testId: string;
  keepOpenOnSelect?: boolean;
}) {
  const [value, setValue] = useState<string | null>(null);
  const options = useMemo<ComboboxOption[]>(
    () =>
      candidates.map((candidate) => ({
        value: candidate.personId,
        label: candidate.label,
        subtitle: candidate.subtitle,
        group: candidate.pool,
      })),
    [candidates],
  );

  return (
    <EntityCombobox
      testId={testId}
      options={options}
      value={value}
      onChange={(personId) => {
        if (!personId) {
          setValue(null);
          return;
        }
        onAssign(personId);
        setValue(null);
      }}
      placeholder={placeholder}
      searchPlaceholder="Search people..."
      emptyMessage="No people found."
      allowClear={false}
      getVisibleOptions={getVisibleCandidateOptions}
      keepOpenOnSelect={keepOpenOnSelect}
    />
  );
}

function AssignmentList({
  series,
  day,
  candidates,
  availabilityByPersonId,
  cameraNumberByAssignmentId,
  onAssign,
  onBookAssignedPerson,
  onUnassign,
  onEditRemark,
}: {
  series: ProjectFunctionSeries;
  day: ProjectFunctionDay;
  candidates: AssignmentCandidate[];
  availabilityByPersonId: Map<string, AvailabilityStatus>;
  cameraNumberByAssignmentId?: Map<string, string> | Record<string, string>;
  onAssign: (functionId: string, personId: string) => void;
  onBookAssignedPerson: (personId: string, functionDate: string) => void;
  onUnassign: (assignmentId: string) => void;
  onEditRemark: (assignmentId: string, currentRemark: string | null) => void;
}) {
  const assignedIds = new Set(
    (day.assignments ?? []).map((assignment) => assignment.person_id),
  );
  const visibleCandidates = candidates.filter(
    (candidate) => !assignedIds.has(candidate.personId),
  );
  const assignedCount = day.assignments?.length ?? 0;
  const effectiveRequiredCount = getEffectiveRequiredCount(series, day);
  const isFull = assignedCount >= effectiveRequiredCount;

  return (
    <div className="space-y-2">
      {(day.assignments ?? []).map((assignment) => {
        const assignmentStatus =
          availabilityByPersonId.get(assignment.person_id) ?? "unknown";
        const showStatusBadge = assignmentStatus !== "booked";
        const cameraNumber = getCameraNumberForAssignment(
          cameraNumberByAssignmentId,
          assignment.id,
        );

        return (
          <div
            key={assignment.id}
            className="flex items-start gap-1 rounded-md bg-primary/10 px-2 py-1 text-xs"
          >
            <div className="flex-1 min-w-0">
              <div className="truncate">
                {assignment.person
                  ? `${assignment.person.first_name} ${assignment.person.last_name}`
                  : "Unknown"}
                {cameraNumber && (
                  <Badge
                    variant="outline"
                    className="ml-1 h-5 px-1.5 align-middle text-[10px]"
                  >
                    {formatCameraAssignmentTag(cameraNumber)}
                  </Badge>
                )}
              </div>
              {assignment.remark && (
                <div className="truncate text-muted-foreground">
                  ({assignment.remark})
                </div>
              )}
              {showStatusBadge && (
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <AvailabilityStatusBadge
                    status={assignmentStatus}
                    className="h-5 px-1.5 text-[10px]"
                  />
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto px-0 py-0 text-[11px]"
                    data-testid={`function-assignment-book-${assignment.id}`}
                    onClick={() =>
                      onBookAssignedPerson(
                        assignment.person_id,
                        day.function_date,
                      )
                    }
                  >
                    Book
                  </Button>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              data-testid={`function-assignment-remark-${assignment.id}`}
              onClick={() => onEditRemark(assignment.id, assignment.remark)}
            >
              <MessageSquare className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-destructive"
              data-testid={`function-assignment-remove-${assignment.id}`}
              onClick={() => onUnassign(assignment.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        );
      })}

      {!isFull && (
        <div
          className="flex items-center gap-2"
          data-testid={`function-day-assign-row-${day.id}`}
        >
          <span
            className="text-xs font-medium text-muted-foreground"
            data-testid={`function-series-day-staffing-${series.id}-${day.function_date}`}
          >
            {assignedCount}/{effectiveRequiredCount}
          </span>
          {visibleCandidates.length > 0 ? (
            <AssignmentAutocomplete
              testId={`function-day-assign-${day.id}`}
              candidates={visibleCandidates}
              placeholder="Assign person"
              onAssign={(personId) => onAssign(day.id, personId)}
              keepOpenOnSelect
            />
          ) : (
            <div className="text-xs text-muted-foreground">
              No available people
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupHeaderCreateFunction({
  selectedDays,
  groupId,
  onCreateFunction,
}: {
  selectedDays: string[];
  groupId?: string | null;
  onCreateFunction: (data: {
    name: string;
    project_function_group_id?: string | null;
    required_count: number;
    selected_days: string[];
    system_tag?: string | null;
  }) => void;
}) {
  const [showInput, setShowInput] = useState(false);
  const [newFunctionName, setNewFunctionName] = useState("");

  const createFunction = () => {
    if (!newFunctionName.trim() || selectedDays.length === 0) return;
    onCreateFunction({
      name: newFunctionName.trim(),
      project_function_group_id: groupId,
      required_count: 1,
      selected_days: selectedDays,
    });
    setNewFunctionName("");
    setShowInput(false);
  };

  if (!showInput) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-full justify-start text-xs text-muted-foreground"
        onClick={() => setShowInput(true)}
      >
        <Plus className="h-3 w-3 mr-1" />
        Add function
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1 p-1">
      <Input
        autoFocus
        placeholder="Function name"
        value={newFunctionName}
        onChange={(e) => setNewFunctionName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && newFunctionName.trim()) {
            createFunction();
          }
          if (e.key === "Escape") {
            setNewFunctionName("");
            setShowInput(false);
          }
        }}
        className="h-7 text-xs"
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        disabled={!newFunctionName.trim()}
        onClick={createFunction}
      >
        <Check className="h-3 w-3" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={() => {
          setNewFunctionName("");
          setShowInput(false);
        }}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

export function FunctionAssignmentMatrix({
  functions,
  groups,
  travelDays,
  availability,
  planningDays,
  eventDays,
  visibleDays,
  viewMode,
  onAssign,
  onBulkAssign,
  onBookAssignedPerson,
  onUnassign,
  onUpdateRemark,
  onDeleteSeries,
  onUpdateSeries,
  onUpdateDayOverride,
  onUpdateGroup,
  onCreateFunction,
  onToggleTravelDay,
  cameraNumberByAssignmentId,
  isUpdating,
}: FunctionAssignmentMatrixProps) {
  const {
    scrollRef: personScrollRef,
    isScrolledX: isPersonScrolledX,
    handleScroll: handlePersonScroll,
  } = useHorizontalScrollState();
  const {
    scrollRef: functionScrollRef,
    isScrolledX: isFunctionScrolledX,
    handleScroll: handleFunctionScroll,
  } = useHorizontalScrollState();
  const [remarkDialog, setRemarkDialog] = useState<{
    assignmentId: string;
    currentRemark: string | null;
  } | null>(null);
  const [editingRemark, setEditingRemark] = useState("");
  const [editingSeries, setEditingSeries] = useState<SeriesEditorState | null>(
    null,
  );
  const { data: eligiblePeople = [] } = usePeople(
    ["employee", "freelancer"],
    false,
  );

  const groupById = useMemo(
    () => new Map(groups.map((group) => [group.id, group] as const)),
    [groups],
  );
  const eventDayValues = useMemo(
    () => eventDays.map((day) => dayToString(day)).sort(),
    [eventDays],
  );

  const personRows = useMemo(
    () =>
      sortByMainFunctionGroup(
        availability
          .filter((person) => person.status !== "not_available")
          .map((person) => ({
            availability: person,
            primaryGroup: getMainFunctionGroupForPerson({
              personId: person.person_id,
              functions,
              groupsById: groupById,
              eventDays: eventDayValues,
            }),
            name: getAvailabilityLabel(person),
          })),
      ),
    [availability, eventDayValues, functions, groupById],
  );

  const assignmentCandidates = useMemo(() => {
    const booked = sortCandidates(
      availability
        .filter((person) => person.status === "booked")
        .map((person) => ({
          personId: person.person_id,
          label: getAvailabilityLabel(person),
          subtitle: "Booked on project",
          pool: "booked" as const,
        })),
    );

    const projectFallback = sortCandidates(
      availability
        .filter(
          (person) =>
            person.status === "available" || person.status === "requested",
        )
        .map((person) => ({
          personId: person.person_id,
          label: getAvailabilityLabel(person),
          subtitle:
            person.status === "available"
              ? "Available on project"
              : "Requested on project",
          pool: "project" as const,
        })),
    );

    const projectIds = new Set(availability.map((person) => person.person_id));
    const globalFallback = sortCandidates(
      eligiblePeople
        .filter((person) => !projectIds.has(person.id))
        .map((person) => ({
          personId: person.id,
          label: getCandidateLabel(person),
          pool: "global" as const,
        })),
    );

    return [...booked, ...projectFallback, ...globalFallback];
  }, [availability, eligiblePeople]);
  const availabilityByPersonId = useMemo(
    () =>
      new Map(
        availability.map(
          (person) => [person.person_id, person.status] as const,
        ),
      ),
    [availability],
  );

  const functionsByDay = useMemo(() => {
    const map = new Map<
      string,
      Array<{ series: ProjectFunctionSeries; day: ProjectFunctionDay }>
    >();
    for (const series of functions) {
      for (const day of series.days) {
        const current = map.get(day.function_date) ?? [];
        current.push({ series, day });
        map.set(day.function_date, current);
      }
    }
    return map;
  }, [functions]);

  const functionSections = useMemo(() => {
    const sections = groups.map((group) => ({
      key: group.id,
      label: group.name,
      group,
      functions: functions.filter(
        (series) => series.project_function_group_id === group.id,
      ),
    }));
    const ungrouped = functions.filter(
      (series) => !series.project_function_group_id,
    );
    if (ungrouped.length > 0) {
      sections.push({
        key: "__ungrouped__",
        label: "Ungrouped",
        group: null,
        functions: ungrouped,
      });
    }
    return sections;
  }, [functions, groups]);

  const travelDayByPersonAndDate = useMemo(() => {
    const map = new Map<string, ProjectPersonTravelDay>();
    for (const travelDay of travelDays) {
      map.set(`${travelDay.person_id}:${travelDay.travel_date}`, travelDay);
    }
    return map;
  }, [travelDays]);

  const handleSaveRemark = () => {
    if (!remarkDialog) return;
    onUpdateRemark(remarkDialog.assignmentId, editingRemark || null);
    setRemarkDialog(null);
  };

  const openEditSeries = (series: ProjectFunctionSeries) => {
    const dayOverrides: Record<string, number | null> = {};
    for (const day of series.days) {
      if (day.required_count_override !== null) {
        dayOverrides[day.function_date] = day.required_count_override;
      }
    }
    setEditingSeries({
      id: series.id,
      name: series.name,
      groupId: series.project_function_group_id ?? null,
      requiredCount: series.required_count,
      selectedDays: [...series.selected_days].sort(),
      dayOverrides,
      systemTag: series.system_tag,
      locationType: series.location_type,
    });
  };

  const handleSaveSeries = (state: SeriesEditorState) => {
    const currentSeries = functions.find((series) => series.id === state.id);
    if (!currentSeries) return;

    const removedDays = currentSeries.days.filter(
      (day) => !state.selectedDays.includes(day.function_date),
    );
    const removedDaysWithAssignments = removedDays.filter(
      (day) => (day.assignments?.length ?? 0) > 0,
    );

    if (
      removedDaysWithAssignments.length > 0 &&
      !window.confirm(
        `Remove ${removedDaysWithAssignments.length} assigned day${
          removedDaysWithAssignments.length === 1 ? "" : "s"
        }? Existing assignments on those days will be deleted.`,
      )
    ) {
      return;
    }

    onUpdateSeries(state.id, {
      name: state.name.trim(),
      project_function_group_id: state.groupId,
      required_count: state.requiredCount,
      selected_days: state.selectedDays,
      system_tag: state.systemTag,
      location_type: state.locationType,
    });

    // Save day overrides for existing days
    for (const day of currentSeries.days) {
      if (state.selectedDays.includes(day.function_date)) {
        const newOverride = state.dayOverrides[day.function_date] ?? null;
        if (newOverride !== day.required_count_override) {
          onUpdateDayOverride(day.id, newOverride);
        }
      }
    }

    setEditingSeries(null);
  };

  const handleDeleteSeries = (series: ProjectFunctionSeries) => {
    if (
      window.confirm(
        `Delete "${series.name}" and all assignments across ${series.days.length} day${
          series.days.length === 1 ? "" : "s"
        }?`,
      )
    ) {
      onDeleteSeries(series.id);
    }
  };

  return (
    <div className="space-y-6">
      {viewMode === "person" ? (
        <div
          ref={personScrollRef}
          onScroll={handlePersonScroll}
          className="relative w-full min-w-0 max-w-full overflow-x-auto"
        >
          <table className="min-w-max caption-bottom text-sm">
            <TableHeader>
              <TableRow>
                <TableHead
                  className={cn(
                    "sticky left-0 z-10 bg-background transition-[width,min-width] duration-150",
                    isPersonScrolledX
                      ? "w-14 min-w-14 max-w-14 text-center"
                      : "min-w-[180px]",
                  )}
                  title="Person"
                >
                  {isPersonScrolledX ? "P" : "Person"}
                </TableHead>
                {visibleDays.map((day) => (
                  <TableHead
                    key={day.toISOString()}
                    className="min-w-[180px] text-center"
                    data-testid={`function-person-day-column-${dayToString(day)}`}
                  >
                    <div className="text-xs">
                      <div>{format(day, "EEEE")}</div>
                      <div className="font-normal">
                        {format(day, "dd.MM.yyyy")}
                      </div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {personRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleDays.length + 1}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No project people yet. Use the function assigners to search
                    booked, available, requested, or fallback staff.
                  </TableCell>
                </TableRow>
              ) : (
                personRows.map(
                  (
                    { availability: personAvailability, primaryGroup },
                    personIndex,
                  ) => {
                    const previousGroup =
                      personRows[personIndex - 1]?.primaryGroup;
                    const showGroupHeader =
                      personIndex === 0 ||
                      previousGroup?.id !== primaryGroup?.id;

                    return (
                      <Fragment key={personAvailability.id}>
                        {showGroupHeader && (
                          <TableRow>
                            <TableCell
                              colSpan={visibleDays.length + 1}
                              className="sticky left-0 z-10 bg-muted/80 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                            >
                              {primaryGroup?.name ?? "Ungrouped"}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow
                          key={personAvailability.id}
                          data-testid={`person-function-row-${personAvailability.person_id}`}
                        >
                          <TableCell
                            className={cn(
                              "sticky left-0 z-10 bg-background font-medium transition-[width,min-width] duration-150",
                              isPersonScrolledX
                                ? "w-14 min-w-14 max-w-14 text-center"
                                : "min-w-[180px]",
                            )}
                          >
                            <div>
                              {personAvailability.person ? (
                                <PersonHoverCard
                                  personId={personAvailability.person.id}
                                  firstName={
                                    personAvailability.person.first_name
                                  }
                                  lastName={personAvailability.person.last_name}
                                  personType={
                                    personAvailability.person.person_type
                                  }
                                  email={personAvailability.person.email}
                                  phone={personAvailability.person.phone}
                                >
                                  <p
                                    className={cn(
                                      isPersonScrolledX
                                        ? "inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold"
                                        : "",
                                    )}
                                    title={`${personAvailability.person.first_name} ${personAvailability.person.last_name}`}
                                  >
                                    {isPersonScrolledX
                                      ? getPersonInitials(
                                          personAvailability.person.first_name,
                                          personAvailability.person.last_name,
                                        )
                                      : `${personAvailability.person.first_name} ${personAvailability.person.last_name}`}
                                  </p>
                                </PersonHoverCard>
                              ) : (
                                <p>Unknown</p>
                              )}
                            </div>
                          </TableCell>
                          {visibleDays.map((day) => {
                            const dayKey = dayToString(day);
                            const travelDay = travelDayByPersonAndDate.get(
                              `${personAvailability.person_id}:${dayKey}`,
                            );
                            const matchingDays =
                              functionsByDay.get(dayKey) ?? [];
                            const assignments = matchingDays.flatMap(
                              ({ series, day }) =>
                                (day.assignments ?? [])
                                  .filter(
                                    (assignment) =>
                                      assignment.person_id ===
                                      personAvailability.person_id,
                                  )
                                  .map((assignment) => ({
                                    assignment,
                                    series,
                                  })),
                            );
                            const unassignedDays = matchingDays.filter(
                              ({ day }) =>
                                (day.assignments?.length ?? 0) <
                                  getEffectiveRequiredCount(
                                    functions.find(
                                      (series) => series.id === day.series_id,
                                    )!,
                                    day,
                                  ) &&
                                !(day.assignments ?? []).some(
                                  (assignment) =>
                                    assignment.person_id ===
                                    personAvailability.person_id,
                                ),
                            );

                            return (
                              <TableCell
                                key={day.toISOString()}
                                className="align-top p-2"
                              >
                                <div className="space-y-1">
                                  {travelDay && (
                                    <button
                                      type="button"
                                      className="flex w-full items-center gap-1 rounded bg-amber-100 p-1.5 text-left text-xs text-amber-900"
                                      onClick={() =>
                                        onToggleTravelDay?.(
                                          personAvailability.person_id,
                                          dayKey,
                                        )
                                      }
                                    >
                                      <Plane className="h-3.5 w-3.5 shrink-0" />
                                      <span className="truncate">Travel</span>
                                    </button>
                                  )}
                                  {assignments.map(({ assignment, series }) => {
                                    const cameraNumber =
                                      getCameraNumberForAssignment(
                                        cameraNumberByAssignmentId,
                                        assignment.id,
                                      );

                                    return (
                                      <div
                                        key={assignment.id}
                                        data-testid={`person-function-assignment-${personAvailability.person_id}-${assignment.id}`}
                                        className="flex items-start gap-1 rounded bg-primary/10 p-1.5 text-xs"
                                      >
                                        <div className="flex-1 min-w-0">
                                          <div className="truncate font-medium">
                                            {series.name}
                                            {cameraNumber && (
                                              <Badge
                                                variant="outline"
                                                className="ml-1 h-5 px-1.5 align-middle text-[10px] font-normal"
                                              >
                                                {formatCameraAssignmentTag(
                                                  cameraNumber,
                                                )}
                                              </Badge>
                                            )}
                                          </div>
                                          {assignment.remark && (
                                            <div className="truncate text-muted-foreground">
                                              ({assignment.remark})
                                            </div>
                                          )}
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-5 w-5"
                                          onClick={() => {
                                            setRemarkDialog({
                                              assignmentId: assignment.id,
                                              currentRemark: assignment.remark,
                                            });
                                            setEditingRemark(
                                              assignment.remark || "",
                                            );
                                          }}
                                        >
                                          <MessageSquare className="h-3 w-3" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-5 w-5 text-destructive"
                                          onClick={() =>
                                            onUnassign(assignment.id)
                                          }
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    );
                                  })}

                                  {(unassignedDays.length > 0 ||
                                    (!travelDay && onToggleTravelDay)) && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          data-testid={`person-day-add-function-${personAvailability.person_id}-${dayKey}`}
                                        >
                                          <Plus className="h-3.5 w-3.5" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        className="w-56 p-1"
                                        align="start"
                                      >
                                        <div className="space-y-0.5">
                                          {unassignedDays.map(
                                            ({ series, day }) => (
                                              <Button
                                                key={day.id}
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-full justify-start text-xs"
                                                data-testid={`person-day-assign-option-${personAvailability.person_id}-${day.id}`}
                                                onClick={() =>
                                                  onAssign(
                                                    day.id,
                                                    personAvailability.person_id,
                                                  )
                                                }
                                              >
                                                {series.name}
                                                <span className="ml-auto text-muted-foreground">
                                                  {day.assignments?.length ?? 0}
                                                  /
                                                  {getEffectiveRequiredCount(
                                                    series,
                                                    day,
                                                  )}
                                                </span>
                                              </Button>
                                            ),
                                          )}
                                          {!travelDay && onToggleTravelDay && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 w-full justify-start text-xs"
                                              onClick={() =>
                                                onToggleTravelDay(
                                                  personAvailability.person_id,
                                                  dayKey,
                                                )
                                              }
                                            >
                                              <Plane className="mr-1 h-3.5 w-3.5" />
                                              Travel
                                            </Button>
                                          )}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </div>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </Fragment>
                    );
                  },
                )
              )}
            </TableBody>
          </table>
        </div>
      ) : (
        <div
          ref={functionScrollRef}
          onScroll={handleFunctionScroll}
          className="relative w-full min-w-0 max-w-full overflow-x-auto"
        >
          <table className="min-w-max caption-bottom text-sm">
            <TableHeader>
              <TableRow>
                <TableHead
                  className={cn(
                    "sticky left-0 z-10 bg-background transition-[width,min-width] duration-150",
                    isFunctionScrolledX
                      ? "w-24 min-w-24 max-w-24"
                      : "min-w-[240px]",
                  )}
                  title="Function"
                >
                  {isFunctionScrolledX ? "Fn" : "Function"}
                </TableHead>
                {visibleDays.map((day) => (
                  <TableHead
                    key={day.toISOString()}
                    className="min-w-[220px] text-center"
                    data-testid={`function-day-column-${dayToString(day)}`}
                  >
                    <div className="text-xs">
                      <div>{format(day, "EEE")}</div>
                      <div className="font-normal">{format(day, "dd.MM")}</div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {functionSections.map((section) => (
                <Fragment key={section.key}>
                  <TableRow key={`${section.key}-header`}>
                    <TableCell
                      colSpan={visibleDays.length + 1}
                      className="sticky left-0 z-10 bg-muted/80 px-3 py-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {section.label}
                          </span>
                          {section.group?.location_type && (
                            <Badge
                              variant="outline"
                              className="text-[10px] normal-case tracking-normal"
                            >
                              {section.group.location_type === "remote"
                                ? "Remote"
                                : "On-site"}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 normal-case tracking-normal">
                          {section.group && onUpdateGroup && (
                            <Select
                              value={section.group.location_type ?? "none"}
                              onValueChange={(value) =>
                                onUpdateGroup(section.group!.id, {
                                  location_type:
                                    value === "none"
                                      ? null
                                      : (value as "remote" | "on_site"),
                                })
                              }
                            >
                              <SelectTrigger className="h-7 w-[128px] bg-background text-xs">
                                <SelectValue placeholder="Location" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  No location
                                </SelectItem>
                                <SelectItem value="on_site">On-site</SelectItem>
                                <SelectItem value="remote">Remote</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {onCreateFunction && visibleDays.length > 0 && (
                            <GroupHeaderCreateFunction
                              selectedDays={visibleDays.map(dayToString)}
                              groupId={
                                section.key === "__ungrouped__"
                                  ? null
                                  : section.key
                              }
                              onCreateFunction={onCreateFunction}
                            />
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                  {section.functions.map((series) => {
                    const daysByDate = new Map(
                      series.days.map((day) => [day.function_date, day]),
                    );
                    return (
                      <TableRow
                        key={series.id}
                        data-testid={`function-series-row-${series.id}`}
                      >
                        <TableCell
                          className={cn(
                            "sticky left-0 z-10 bg-background align-top transition-[width,min-width] duration-150",
                            isFunctionScrolledX
                              ? "w-24 min-w-24 max-w-24"
                              : "min-w-[240px]",
                          )}
                        >
                          <div
                            className={cn(
                              "flex items-center justify-between gap-2",
                              isFunctionScrolledX && "justify-center",
                            )}
                            data-testid={`function-series-summary-${series.id}`}
                          >
                            <div className="font-medium flex min-w-0 items-center gap-1.5">
                              {series.system_tag === "camera_position" && (
                                <Video
                                  className="h-4 w-4 text-muted-foreground"
                                  aria-hidden="true"
                                />
                              )}
                              <span className="truncate" title={series.name}>
                                {series.name} ({series.required_count})
                              </span>
                              {series.location_type && !isFunctionScrolledX && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1 py-0"
                                >
                                  {series.location_type === "remote"
                                    ? "Remote"
                                    : "On-site"}
                                </Badge>
                              )}
                            </div>
                            <div
                              className={cn(
                                "flex items-center gap-0.5",
                                isFunctionScrolledX && "hidden",
                              )}
                            >
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    title="Bulk assign"
                                    data-testid={`function-series-bulk-assign-${series.id}`}
                                  >
                                    <BulkAssignIcon className="h-3.5 w-3.5" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-56 p-2"
                                  data-testid={`function-series-bulk-assign-popover-${series.id}`}
                                >
                                  <AssignmentAutocomplete
                                    testId={`function-series-bulk-assign-combobox-${series.id}`}
                                    candidates={assignmentCandidates}
                                    placeholder="Bulk assign person"
                                    onAssign={(personId) =>
                                      onBulkAssign(
                                        series.id,
                                        personId,
                                        series.days.map((day) => day.id),
                                      )
                                    }
                                  />
                                </PopoverContent>
                              </Popover>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    data-testid={`function-series-menu-${series.id}`}
                                  >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    data-testid={`function-series-edit-${series.id}`}
                                    onClick={() => openEditSeries(series)}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    data-testid={`function-series-delete-${series.id}`}
                                    onClick={() => handleDeleteSeries(series)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </TableCell>

                        {visibleDays.map((day) => {
                          const dayRecord = daysByDate.get(dayToString(day));
                          if (!dayRecord) {
                            return (
                              <TableCell
                                key={day.toISOString()}
                                className="bg-muted/20"
                                data-testid={`function-series-day-${series.id}-${dayToString(day)}`}
                              />
                            );
                          }

                          const effectiveRequiredCount =
                            getEffectiveRequiredCount(series, dayRecord);

                          return (
                            <TableCell
                              key={day.toISOString()}
                              className="align-top"
                              data-testid={`function-series-day-${series.id}-${dayRecord.function_date}`}
                            >
                              <div className="space-y-2">
                                <AssignmentList
                                  series={series}
                                  day={dayRecord}
                                  candidates={assignmentCandidates}
                                  availabilityByPersonId={
                                    availabilityByPersonId
                                  }
                                  cameraNumberByAssignmentId={
                                    cameraNumberByAssignmentId
                                  }
                                  onAssign={onAssign}
                                  onBookAssignedPerson={onBookAssignedPerson}
                                  onUnassign={onUnassign}
                                  onEditRemark={(
                                    assignmentId,
                                    currentRemark,
                                  ) => {
                                    setRemarkDialog({
                                      assignmentId,
                                      currentRemark,
                                    });
                                    setEditingRemark(currentRemark || "");
                                  }}
                                />
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {section.functions.length === 0 && (
                    <TableRow>
                      <TableCell
                        className={cn(
                          "sticky left-0 z-10 bg-background align-top transition-[width,min-width] duration-150",
                          isFunctionScrolledX
                            ? "w-24 min-w-24 max-w-24"
                            : "min-w-[240px]",
                        )}
                      />
                      {visibleDays.map((day) => (
                        <TableCell
                          key={day.toISOString()}
                          className="bg-muted/20"
                        />
                      ))}
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </table>
        </div>
      )}

      <SeriesEditorDialog
        open={!!editingSeries}
        onOpenChange={(open) => !open && setEditingSeries(null)}
        state={editingSeries}
        planningDays={planningDays}
        eventDays={eventDays}
        existingDays={
          editingSeries
            ? new Map(
                (
                  functions.find((s) => s.id === editingSeries.id)?.days ?? []
                ).map((day) => [day.function_date, day] as const),
              )
            : new Map()
        }
        groups={groups}
        onSave={handleSaveSeries}
      />

      <Dialog open={!!remarkDialog} onOpenChange={() => setRemarkDialog(null)}>
        <DialogContent
          className="sm:max-w-sm"
          data-testid="function-remark-dialog"
        >
          <DialogHeader>
            <DialogTitle>Edit Remark</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              data-testid="function-remark-input"
              placeholder="e.g., Camera 1"
              value={editingRemark}
              onChange={(e) => setEditingRemark(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                data-testid="function-remark-cancel"
                onClick={() => setRemarkDialog(null)}
              >
                Cancel
              </Button>
              <Button
                data-testid="function-remark-save"
                onClick={handleSaveRemark}
                disabled={isUpdating}
              >
                <Check className="mr-2 h-4 w-4" />
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
